import { ReportData } from '@g17eco/types/reportData';
import { GoogleChartWrapper, ReactGoogleChartEvent } from 'react-google-charts/dist/types';
import { DATE, formatDate } from '@utils/date';
import { DataSource } from '@features/report-output/common';

export const CHART_DEFAULTS = {
  OPTIONS: {
    vAxis: {
      format: 'decimal',
      gridlines: { count: 4 },
    },
    colors: ['#A6CFE5', '#D6F5DF', '#1A5A87'],
    pieSliceTextStyle: {
      color: '#0c3866',
    },
    legend: {
      position: 'none',
    },
    chartArea: {
      top: 50,
      bottom: 20,
      left: 80,
      right: 20,
    },
    titleTextStyle: {
      color: 'a1a3a4',
    },
  },
};

export interface GenerateChartsProps {
  updateData: (key: string, data: DataSource) => void;
  questionData: ReportData[];
}

interface ChartReadyHandler {
  key: keyof EsgDataSources;
  data: DataSource;
  handler: (key: string, data: DataSource) => void;
}

export const chartReadyEvent = (params: ChartReadyHandler): ReactGoogleChartEvent => {
  const { key, data, handler } = params;
  return {
    eventName: 'ready',
    callback: (ChartRef: { chartWrapper: GoogleChartWrapper }) => {
    data.chart = (ChartRef.chartWrapper.getChart().getImageURI() as unknown as string).split(',')[1];
    handler(key, data);
  }};
};

export const getReportEffectiveDate = (questionData: ReportData[], utrCode: string) => {
  const utrv = questionData.find((d) => d.universalTracker.code === utrCode);
  const label = utrv?.effectiveDate ? formatDate(utrv.effectiveDate, DATE.MONTH_YEAR_SHORT, true) : '';
  return label;
}

export interface GHGEmissionsDataSource extends DataSource {
  totalEmissions?: number;
}

export interface EnergyConsumptionDataSource extends DataSource {
  consumption?: number;
  unit?: string;
}

export interface EnergyIntensityRatioDataSource extends DataSource {
  ratio?: number;
}

export interface WaterConsumptionDataSource extends DataSource {
  consumption?: number;
  unit?: string;
}

export interface WasteGeneratedDataSource extends DataSource {
  hazardousWaste?: number;
  nonHazardousWaste?: number;
}

export interface GenderSplitDataSource extends DataSource {
  femaleEmployees?: number;
  maleEmployees?: number;
}

export interface GenderSplitPcDataSource extends DataSource {
  femaleEmployeesPc?: number;
  maleEmployeesPc?: number;
  genderRatio?: number;
}

export interface EmployeeCategory {
  loaded: boolean;
  width: number;
  height: number;
  name: string;
  chart: string;
  malePay: number;
  femalePay: number;
  payRatio: number;
}

export interface GenderPayGapDataSource extends DataSource {
  employeeCategories?: EmployeeCategory[];
}

export interface AverageTrainingDataSource extends DataSource {
  maleHours?: number;
  femaleHours?: number;
}

export interface BoardGenderDataSource extends DataSource {
  male?: number;
  female?: number;
}

export interface BoardDiversityDataSource extends DataSource {
  disabilityTotal?: number;
  minorityTotal?: number;
  otherTotal?: number;
}

export interface EsgDataSources {
  ghgEmissions: GHGEmissionsDataSource;
  energyConsumption: EnergyConsumptionDataSource;
  energyIntensityRatio: EnergyIntensityRatioDataSource;
  waterConsumption: WaterConsumptionDataSource;
  wasteGenerated: WasteGeneratedDataSource;
  genderSplit: GenderSplitDataSource;
  genderSplitPc: GenderSplitPcDataSource;
  genderPayGap: GenderPayGapDataSource;
  averageTraining: AverageTrainingDataSource;
  boardGender: BoardGenderDataSource;
  boardDiversity: BoardDiversityDataSource;
}
